import Image from "next/image";
import { businessMenuItems, manageMenuItems, resourcesMenuItems } from './menu-items'
import Link from "next/link";
import { useEffect, useState } from "react";
import StartABusinessDiagram from './StartABusinessDiagram';
import AdditionalManageBusinessItems from "./AdditionalManageBusinessItems";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import "../../public-v33.css";


interface BusinessMenuItem {
  name: string;
  href: string;
  description?: string;
  icon: string;
}

interface MegaMenuProps {
  className?: string;
  selectedMenu?: string;
}

export default function MegaMenu({ className, selectedMenu }: MegaMenuProps) {
  const [items, setItems] = useState<BusinessMenuItem[]>([]);


  useEffect(() => {
    switch (selectedMenu) {
      case 'start-business':
        setItems(businessMenuItems)
        break;
      case 'manage':
        setItems(manageMenuItems)
        break;
      case 'resources':
        setItems(resourcesMenuItems)
        break;
      default:
        setItems([])
        break;
    }
  }, [selectedMenu])


  return (
    <div className={`${className ?? ''}`}>
      <div className="v33-mega min-w-[1380px] h-[600px] relative">
        {/* Left section with gradient */}
        <div className="absolute left-0 top-0 w-96 h-full flex flex-col">
          {/* Gradient background */}
          <div className="absolute inset-0 opacity-20 bg-gradient-to-b from-[#22388d] to-transparent" />

          {/* Content inside gradient */}
          <div className="relative z-10 pt-[43px] px-10">
            <h2 className="v33-serif text-3xl font-semibold leading-10 text-center text-[#101c46]">
              {
                selectedMenu === "start-business" ? (
                  <>Start a Business<br />Fast, Smart & Simple</>
                ) : (<>Stay Compliant, Stay<br /> Protected </>)
              }
            </h2>
          </div>
        </div>

        {/* Background image positioned below gradient section */}
        <Image
          src={selectedMenu === "start-business" ? "/assets/public/landing/mega-menu-start-a-business.webp" : "/assets/public/landing/mega-menu-manage-a-business.webp"}
          alt="Business people"
          width={320}
          height={300}
          className="absolute left-10 top-[80px]"
        />

        {/* New Business Guide Card - Only for start-business */}
        {selectedMenu === 'start-business' && (
          <div className="absolute left-10 top-[400px] w-80 z-20">
            <div className="bg-gradient-to-r from-[#101c46] to-[#22388d] rounded-3xl p-6 text-white flex flex-col items-center">
              <h3 className="text-lg font-bold mb-2 text-center">
                NEW BUSINESS GUIDE
              </h3>
              <p className="text-xs text-center mb-4 opacity-90">
                Step-by-step help to launch your business.
              </p>
              <Button
                asChild
                className="bg-[#22388d] hover:bg-[#1a3a8f] text-white font-semibold rounded-xl transition-colors mx-auto"
              >
                <Link href="/new-business-guide.html" className="flex items-center justify-center gap-2">
                  Open the Guide
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Menu items and diagram section */}
        <div className="absolute left-[420px] top-8 flex">
          {/* First column - Menu items */}
          <div className="flex flex-col gap-8">
            {items.map((item) => (
              <div key={item.name} className="flex items-center gap-7">
                <div className="w-12 h-12 bg-slate-100 rounded-[30px] flex items-center justify-center flex-shrink-0">
                  <Image
                    src={item.icon}
                    alt={`${item.name} icon`}
                    width={28}
                    height={28}
                    className="w-7 h-7"
                  />
                </div>
                <div className="flex flex-col">
                  <div className="v33-sans text-[#101c46] text-base font-semibold leading-7 hover:text-[#a0782a]">
                    <Link href={item.href}>{item.name}</Link>
                  </div>
                  {item.description && (
                    <div className="text-[#5a6280] text-sm font-normal leading-5 opacity-90">
                      {item.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="mx-16 w-px h-[480px] bg-zinc-200 opacity-60" />

          {/* Second column - Business Formation Diagram (only for start-business) */}
          {selectedMenu === 'start-business' && (
            <div className="mr-8">
              <StartABusinessDiagram />
            </div>
          )}
          {selectedMenu === 'manage' && (
            <div className="mr-8">
              <AdditionalManageBusinessItems />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}